import { keys } from "@/lib/i18n/portal";
import type {
  StoreSubscriptionPaymentReviewStatus,
  StoreSubscriptionPaymentView,
} from "@/lib/api/types";
import { STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS } from "@/lib/api/types";
import { SubscriptionPaymentProofModal } from "@/shared/stores/components/subscriptions/SubscriptionPaymentProofModal";
import { TopUpReviewActions } from "@/shared/stores/components/subscriptions/TopUpReviewActions";
import { formatDate as formatPortalDate } from "@/lib/formatting/date-time";
import { StatusDot, type DotTone } from "@/shared/rd";
import { Eyebrow, Meta } from "@/shared/stores/components/rd-local";

type Props = {
  storeId: string;
  locale: string;
  /** Seller-submitted rows only — an admin-recorded payment is not a request. */
  requests: StoreSubscriptionPaymentView[];
  t: (key: string) => string;
};

const STATUS_TONES: Record<StoreSubscriptionPaymentReviewStatus, DotTone> = {
  [STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS.PENDING]: "blue",
  [STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS.CONFIRMED]: "green",
  [STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS.REJECTED]: "red",
};

/**
 * The seller's wallet top-up requests, and the two actions an admin can take.
 *
 * Confirm is the only path that moves money — the backend credits the wallet
 * and settles held orders in the same transaction — so the hint says exactly
 * that before the click rather than after. The two actions themselves live in
 * `TopUpReviewActions`, shared with the cross-store review queue so the store
 * page and the queue can never drift apart on what a review costs.
 *
 * Never colour alone: every state is a dot AND its word.
 *
 * Renders content only (title, hint, rows): the caller places it inside the
 * wallet card.
 */
export function StoreTopUpRequestsPanel({
  storeId,
  locale,
  requests,
  t,
}: Props) {
  const statusLabels: Record<StoreSubscriptionPaymentReviewStatus, string> = {
    [STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS.PENDING]: t(
      keys.admin.subscriptionTopUpStatusPending,
    ),
    [STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS.CONFIRMED]: t(
      keys.admin.subscriptionTopUpStatusConfirmed,
    ),
    [STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS.REJECTED]: t(
      keys.admin.subscriptionTopUpStatusRejected,
    ),
  };

  const formatAmount = (amount: number, currency: string) => {
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency || "EGP",
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return `${amount} ${currency}`.trim();
    }
  };

  const formatDate = (value: string | null) => {
    if (!value) {
      return null;
    }
    const formatted = formatPortalDate(value, locale, "");
    return formatted || null;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <Eyebrow>{t(keys.admin.subscriptionTopUpRequestsTitle)}</Eyebrow>
      <Meta style={{ fontSize: 12, color: "var(--rd-text-tertiary)" }}>
        {t(keys.admin.subscriptionTopUpRequestsHint)}
      </Meta>

      {requests.length === 0 ? (
        <Meta>{t(keys.admin.subscriptionTopUpRequestsEmpty)}</Meta>
      ) : (
        requests.map((request) => {
          const isPending =
            request.reviewStatus === STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS.PENDING;
          const sentOn = formatDate(request.requestedAt ?? request.paidAt);
          const meta = [
            request.requestedBySeller
              ? `${t(keys.admin.subscriptionTopUpRequestedBy)}: ${request.requestedBySeller.name}`
              : null,
            sentOn ? `${t(keys.admin.subscriptionTopUpRequestedAt)}: ${sentOn}` : null,
            request.settledByAdmin
              ? `${t(keys.admin.subscriptionTopUpReviewedBy)}: ${request.settledByAdmin.name}`
              : null,
          ].filter(Boolean);
          return (
            <div
              key={request.id}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                padding: "9px 12px",
                borderRadius: "var(--rd-radius-lg)",
                background: "var(--rd-surface-sunken)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <StatusDot tone={STATUS_TONES[request.reviewStatus]}>
                  {statusLabels[request.reviewStatus]}
                </StatusDot>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: "var(--rd-text-secondary)" }} dir="auto">
                  {meta.join(" · ")}
                </span>
                <span className="rd-num" style={{ fontWeight: 600 }}>
                  {formatAmount(request.amount, request.currency)}
                </span>
              </div>

              {request.reviewReason ? (
                <Meta>
                  {t(keys.admin.subscriptionTopUpRejectReasonLabel)}: {request.reviewReason}
                </Meta>
              ) : null}

              <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-start" }}>
                {request.proofObjectKey ? (
                  <SubscriptionPaymentProofModal storeId={storeId} paymentId={request.id} />
                ) : (
                  <span style={{ fontSize: 12, color: "var(--rd-text-tertiary)" }}>
                    {t(keys.admin.subscriptionPaymentProofMissing)}
                  </span>
                )}
                {isPending ? (
                  <TopUpReviewActions storeId={storeId} paymentId={request.id} t={t} />
                ) : null}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
